import { t } from "@lingui/macro";
import { useState } from "react";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import {
  BellIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  CheckCircleIcon,
  EnvelopeSimpleIcon,
  ExportIcon,
  EyeIcon,
  FunnelIcon,
  LightbulbFilamentIcon,
  MagnifyingGlassIcon,
  UserCheckIcon,
  UserPlusIcon,
  UsersIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { useFilterAmbassador } from "@/client/services/user/ambassadors";
import { userFilterAmbassadorStore } from "@/client/stores/filter-ambassador";
import { useDialog } from "@/client/stores/dialog";
import { useExportAmbassador } from "@/client/services/user/export-ambassador-xlsx";
import { Role } from "@reactive-resume/utils";
import { useChangeActivation } from "@/client/services/user/activate-user";
import { useSendVerificationEmailByAdmin } from "@/client/services/auth";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";

export const AmbaAdminPage = () => {
  const { filterAmbassador, replaceFilterAmbassador } = userFilterAmbassadorStore();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "inactive">("all");
  const [verified, setVerified] = useState<"all" | "yes" | "no">("all");

  const { filterAmbassador: filterAmbassadors } = useFilterAmbassador(page, filterAmbassador);
  const { refetch } = useExportAmbassador();

  const { open } = useDialog("filter-ambassador");
  const { open: openGetUser } = useDialog("get-user");
  const dialogCreate = useDialog<{ role: number }>("create-user");

  const { ChStatus } = useChangeActivation();
  const { verificationEmaiByAdmin } = useSendVerificationEmailByAdmin();

  const handleBackpage = () => setPage(page - 1);
  const handleForwardpage = () => setPage(page + 1);
  const handleSpecficpage = (newpage: number) => setPage(newpage);

  const applyQuickFilters = () => {
    const nextFilter = { ...filterAmbassador };
    delete nextFilter.name;
    delete nextFilter.email;
    delete nextFilter.isActivated;
    delete nextFilter.emailVerified;

    if (search.trim()) {
      const searchField = search.includes("@") ? "email" : "name";
      nextFilter[searchField] = { contains: search.trim() };
    }
    if (status !== "all") nextFilter.isActivated = { equals: status === "active" };
    if (verified !== "all") nextFilter.emailVerified = { equals: verified === "yes" };

    setPage(1);
    replaceFilterAmbassador(nextFilter);
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setVerified("all");
    setPage(1);
    replaceFilterAmbassador(null);
  };

  // Colonnes STRICTEMENT basées sur AmbaTable
  const columns: Column<any>[] = [
    {
      key: "name",
      header: "Name",
      render: (item) => <span className="font-medium text-admin-text-primary">{item.name}</span>,
    },
    {
      key: "email",
      header: "Email",
      render: (item) => <span className="text-admin-text-secondary">{item.email}</span>,
    },
    {
      key: "createdAt",
      header: "Registration date",
      render: (item) => (
        <span className="text-admin-text-secondary">{item.createdAt.toDateString()}</span>
      ),
    },
    {
      key: "updatedAt",
      header: t`Dernière modification`,
      render: (item) => (
        <span className="text-admin-text-secondary">{item.updatedAt.toDateString()}</span>
      ),
    },
    {
      key: "emailVerified",
      header: "Email Verified",
      render: (item) =>
        item.emailVerified ? (
          <CheckCircleIcon size={20} weight="fill" className="text-admin-success" />
        ) : (
          <XCircleIcon size={20} weight="fill" className="text-admin-danger" />
        ),
    },
    {
      key: "isActivated",
      header: "Activated",
      render: (item) =>
        item.isActivated ? (
          <CheckCircleIcon size={20} weight="fill" className="text-admin-success" />
        ) : (
          <XCircleIcon size={20} weight="fill" className="text-admin-danger" />
        ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (item) => (
        <div className="flex justify-end gap-2 text-admin-text-secondary">
          {/* Voir détails */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="View user"
            className="hover:text-admin-text-primary"
            onClick={() => openGetUser("read", { id: "get-user", item: { id: item.id } })}
          >
            <EyeIcon size={18} />
          </Button>

          {/* Activer / Désactiver */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle activation"
            className={
              item.isActivated
                ? "hover:text-admin-warning"
                : "text-admin-success hover:text-admin-success"
            }
            onClick={() => ChStatus(item.id)}
          >
            <LightbulbFilamentIcon size={18} />
          </Button>

          {/* Envoyer vérification email */}
          <Button
            disabled={item.emailVerified}
            variant="ghost"
            size="icon"
            aria-label="Send verification"
            className="hover:text-admin-text-primary"
            onClick={() => verificationEmaiByAdmin(item.email)}
          >
            <EnvelopeSimpleIcon size={18} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Header de page */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">Ambassadeurs</h1>
          <p className="mt-1 text-sm text-admin-text-muted">{t`Tableau de bord / Ambassadeurs`}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="text"
              placeholder={t`Rechercher des ambassadeurs…`}
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Notifications"
            className="rounded-full bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="h-8 w-8 overflow-hidden rounded-full bg-admin-primary">
            <img src="/avatar.jpg" alt={t`Utilisateur`} className="h-full w-full object-cover" />
          </div>
        </div>
      </div>

      {/* Cartes de Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard
              text="Ambassadeurs"
              number={filterAmbassadors?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-surface-elevated p-3 text-admin-text-secondary">
              <UsersIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 4.2%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Activés"
              number={
                filterAmbassadors?.data?.filter((user) => user.isActivated).length.toString() ?? "0"
              }
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-success/20 p-3 text-admin-success">
              <UserCheckIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 3.7%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Emails vérifiés"
              number={
                filterAmbassadors?.data?.filter((user) => user.emailVerified).length.toString() ??
                "0"
              }
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-accent/20 p-3 text-admin-accent">
              <EnvelopeSimpleIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 1.2%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Non activés"
              number={
                filterAmbassadors?.data?.filter((user) => !user.isActivated).length.toString() ??
                "0"
              }
              classNameText=""
              classNameNumber=""
            />
            <div className="bg-admin-warning/20 text-admin-warning rounded-full p-3">
              <UserPlusIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-danger">▼ 1.3%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>
      </div>

      {/* Section Filtres et Actions */}
      <div className="mb-8 rounded-xl-admin border border-admin-border bg-admin-surface p-6">
        <div className="flex flex-col flex-wrap justify-between gap-4 lg:flex-row">
          {/* Recherche */}
          <div className="relative min-w-[250px] flex-1">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="text"
              placeholder={t`Rechercher par nom ou adresse e-mail…`}
              className="h-10 w-full bg-admin-background pl-10 text-admin-text-primary"
            />
          </div>

          {/* Filtres Selects */}
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">{t`Statut`}</span>
              <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
                <SelectTrigger className="h-10 min-w-32 bg-admin-background text-admin-text-secondary">
                  <SelectValue placeholder={t`Tous les statuts`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`Tous les statuts`}</SelectItem>
                  <SelectItem value="active">{t`Actif`}</SelectItem>
                  <SelectItem value="inactive">{t`Inactif`}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Verified</span>
              <Select
                value={verified}
                onValueChange={(value) => setVerified(value as typeof verified)}
              >
                <SelectTrigger className="h-10 min-w-24 bg-admin-background text-admin-text-secondary">
                  <SelectValue placeholder={t`Tous`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`Tous`}</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-3">
              <Button
                variant="outline"
                onClick={applyQuickFilters}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Filter
              </Button>
              <Button
                variant="outline"
                onClick={() => open("create", { id: "filter-ambassador" })}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Advanced filters
              </Button>
              <Button
                variant="ghost"
                onClick={resetFilters}
                className="h-10 text-admin-text-muted hover:text-admin-text-primary"
              >
                Reset
              </Button>
              <Button
                variant="outline"
                onClick={() => refetch()}
                className="h-10 bg-admin-background text-admin-text-secondary hover:text-admin-text-primary"
              >
                <ExportIcon size={16} className="mr-2" /> Export
              </Button>
              <Button
                onClick={() =>
                  dialogCreate.open("create", { id: "create-user", item: { role: Role.PromoUser } })
                }
                className="h-10 bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90"
              >
                <UserPlusIcon size={16} className="mr-2" />
                {t`Nouvel utilisateur`}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau & Pagination */}
      <DataTable
        title=""
        data={filterAmbassadors?.data ?? []}
        columns={columns}
        pageSize={10}
        className="shadow-sm-admin"
      />

      {/* Pagination (Style UserAdminPage) */}
      <div className="my-3 flex flex-row justify-end space-x-3">
        {page > 1 && (
          <Button variant="outline" onClick={handleBackpage} className="rounded-lg">
            <CaretCircleLeftIcon size={32} weight="regular" />
          </Button>
        )}
        {page - 2 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 2)}
            className="rounded-lg"
          >
            {page - 2}
          </Button>
        )}
        {page - 1 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 1)}
            className="rounded-lg"
          >
            {page - 1}
          </Button>
        )}

        <Button variant="secondary" className="bg-admin-primary text-admin-primary-foreground">
          {page}
        </Button>

        {filterAmbassadors && page + 1 <= filterAmbassadors.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 1)}
            className="rounded-lg"
          >
            {page + 1}
          </Button>
        )}
        {filterAmbassadors && page + 2 <= filterAmbassadors.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 2)}
            className="rounded-lg"
          >
            {page + 2}
          </Button>
        )}
        {filterAmbassadors && page < filterAmbassadors.page && (
          <Button variant="outline" onClick={handleForwardpage} className="rounded-lg">
            <CaretCircleRightIcon size={32} weight="regular" />
          </Button>
        )}
      </div>
    </>
  );
};
